/**
 * Camera Orbit & Cinematic Flight Controller
 * Provides buttery-smooth OrbitControls with damping, touch gestures,
 * and cinematic camera swoops to targeted Selenographic landmarks.
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { latLonToVector3 } from '../utils/math.js';

export class OrbitManager {
  constructor(camera, domElement, sphereRadius = 5.0) {
    this.camera = camera;
    this.domElement = domElement;
    this.radius = sphereRadius;

    this.controls = new OrbitControls(this.camera, this.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.screenSpacePanning = true;

    // Zoom limits: from right above surface (0.3 radius altitude) to deep space (8x radius)
    this.controls.minDistance = this.radius * 1.08;
    this.controls.maxDistance = this.radius * 8.0;

    // Flight animation state
    this.isFlying = false;
    this.flightStartPos = new THREE.Vector3();
    this.flightEndPos = new THREE.Vector3();
    this.flightStartTime = 0;
    this.flightDuration = 1800; // ms
    this.onFlightComplete = null;
  }

  update() {
    if (this.isFlying) {
      const now = performance.now();
      const elapsed = now - this.flightStartTime;
      const progress = Math.min(1.0, elapsed / this.flightDuration);

      // Smooth ease-in-out cubic easing
      const t = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      // Slerp or arched trajectory to avoid cutting through the Moon
      const currentPos = new THREE.Vector3().lerpVectors(this.flightStartPos, this.flightEndPos, t);
      // Ensure distance never dips below minimum safe radius during transit
      const currentDist = currentPos.length();
      const startDist = this.flightStartPos.length();
      const endDist = this.flightEndPos.length();
      const minSafeDist = Math.max(this.radius * 1.6, Math.min(startDist, endDist));

      if (currentDist < minSafeDist) {
        currentPos.normalize().multiplyScalar(minSafeDist);
      }

      this.camera.position.copy(currentPos);
      this.camera.lookAt(0, 0, 0);
      this.controls.target.set(0, 0, 0);

      if (progress >= 1.0) {
        this.isFlying = false;
        if (this.onFlightComplete) {
          this.onFlightComplete();
          this.onFlightComplete = null;
        }
      }
    }

    this.controls.update();
  }

  flyTo(lat, lon, targetAltitudeRatio = 1.35, callback = null) {
    const targetVector = latLonToVector3(lat, lon, this.radius * targetAltitudeRatio);

    this.flightStartPos.copy(this.camera.position);
    this.flightEndPos.set(targetVector.x, targetVector.y, targetVector.z);
    this.flightStartTime = performance.now();
    this.isFlying = true;
    this.onFlightComplete = callback;
  }

  resetToNearside() {
    // 0° Lon, 0° Lat (Bruce Crater / Sinus Medii) facing camera along +Z
    this.flyTo(0, 0, 2.5);
  }

  flipToFarside() {
    // 180° Lon, 0° Lat
    this.flyTo(0, 180, 2.5);
  }

  resetToNorthPole() {
    this.flyTo(89, 0, 2.2);
  }

  resetToSouthPole() {
    this.flyTo(-89, 0, 2.2);
  }

  getDistance() {
    return this.camera.position.distanceTo(this.controls.target);
  }
}
